import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, TrendingUp, FileDown, History, FileText, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const dl = (dataUrl, filename, action = 'download') => {
  const bin = atob(dataUrl.split(',')[1]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  if (action === 'download') {
    const a = document.createElement('a'); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  } else if (action === 'print') {
    const w = window.open(url, '_blank'); if (w) w.onload = () => w.print();
  } else {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const empty = {
  investment_name: '', name: '', father_husband_name: '', cnic: '', address: '', mobile: '',
  investment_amount: '', profit_amount: '', profit_frequency: 'Monthly', profit_credit_day: 1,
};

export default function InvestorsManagement() {
  const { user } = useAuth();
  const canDelete = user?.role === 'Super Admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [history, setHistory] = useState({ open: false, id: null, rows: [] });
  const [slip, setSlip] = useState({ open: false, id: null, slip_type: 'credit', amount: '', amount_words: '', checked_by: '', authority_name: '' });
  const [tx, setTx] = useState({ open: false, id: null, type: 'additional_investment', amount: '', note: '' });

  const fetchAll = async () => {
    try { setLoading(true); const { data } = await axios.get(`${API_URL}/api/investors`, { withCredentials: true }); setRows(data); }
    catch { toast.error('Failed to load investors'); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, investment_amount: Number(form.investment_amount), profit_amount: Number(form.profit_amount || 0), profit_credit_day: Number(form.profit_credit_day || 1) };
      await axios.post(`${API_URL}/api/investors`, payload, { withCredentials: true });
      toast.success('Investor onboarded'); setDialog(false); setForm(empty); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const openHistory = async (id) => {
    setHistory({ open: true, id, rows: [] });
    const { data } = await axios.get(`${API_URL}/api/investors/${id}/history`, { withCredentials: true });
    setHistory({ open: true, id, rows: data });
  };

  const printStatement = async (id, action = 'view') => {
    try {
      const { data } = await axios.get(`${API_URL}/api/investors/${id}/statement`, { withCredentials: true });
      dl(data.pdf, `Investor_Statement_${data.account_number || id}.pdf`, action);
      toast.success(action === 'download' ? 'Statement downloaded' : action === 'print' ? 'Opening for print…' : 'Statement opened');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate statement');
    }
  };

  const submitTx = async () => {
    try {
      await axios.post(`${API_URL}/api/investors/${tx.id}/transaction`,
        { type: tx.type, amount: Number(tx.amount), note: tx.note }, { withCredentials: true });
      toast.success('Transaction recorded');
      setTx({ open: false, id: null, type: 'additional_investment', amount: '', note: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitSlip = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/investors/${slip.id}/slip`,
        { slip_type: slip.slip_type, amount: Number(slip.amount), amount_words: slip.amount_words, checked_by: slip.checked_by, authority_name: slip.authority_name },
        { withCredentials: true });
      dl(data.pdf, `${slip.slip_type}_slip_${data.slip_number}.pdf`);
      toast.success(`${slip.slip_type} slip generated`);
      setSlip({ open: false, id: null, slip_type: 'credit', amount: '', amount_words: '', checked_by: '', authority_name: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (r) => {
    const label = `${r.name}${r.account_number ? ` (${r.account_number})` : ''}`;
    if (!window.confirm(`Delete investor ${label}?\n\nThis permanently removes the investor and every transaction / additional investment / withdrawal record attached to them. This action cannot be undone.`)) return;
    try {
      const { data } = await axios.delete(`${API_URL}/api/investors/${r.id}`, { withCredentials: true });
      toast.success(`Investor deleted (${data.transactions_removed} transaction${data.transactions_removed === 1 ? '' : 's'} removed)`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="investors-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="investors-title">Investor Accounts</h1>
          <p className="text-slate-400">Onboard investors, log additional investments, generate credit/debit slips</p>
        </div>
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-investor-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Investor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl">
            <DialogHeader><DialogTitle>Onboard Investor</DialogTitle>
              <DialogDescription className="text-slate-400">Capture investment details and profit terms.</DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              {[
                ['investment_name', 'Investment Name *', 'text'],
                ['name', 'Investor Name *', 'text'],
                ['father_husband_name', 'Father / Husband Name', 'text'],
                ['cnic', 'CNIC *', 'text'],
                ['mobile', 'Mobile *', 'text'],
                ['investment_amount', 'Investment Amount (PKR) *', 'number'],
                ['profit_amount', 'Profit Amount (PKR)', 'number'],
                ['profit_credit_day', 'Profit Credit Day (1-28)', 'number'],
              ].map(([k, l, t]) => (
                <div key={k} className="space-y-2">
                  <Label className="text-slate-300">{l}</Label>
                  <Input type={t} required={l.endsWith('*')} value={form[k]}
                    data-testid={`investor-${k}`}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
              ))}
              <div className="space-y-2">
                <Label className="text-slate-300">Profit Frequency</Label>
                <Select value={form.profit_frequency} onValueChange={(v) => setForm({ ...form, profit_frequency: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="investor-profit_frequency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white">
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-slate-300">Address</Label>
                <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" rows={2} />
              </div>
              <div className="col-span-2 flex justify-end space-x-2">
                <Button type="button" variant="ghost" onClick={() => setDialog(false)} className="text-slate-300">Cancel</Button>
                <Button type="submit" data-testid="submit-investor-btn" className="bg-emerald-600 hover:bg-emerald-700">Onboard</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          {loading ? <div className="text-center py-8 text-slate-400">Loading...</div>
            : rows.length === 0 ? <div className="text-center py-8 text-slate-400" data-testid="no-investors">No investors yet</div>
            : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Account #</TableHead>
                    <TableHead className="text-slate-400">Investment</TableHead>
                    <TableHead className="text-slate-400">Investor</TableHead>
                    <TableHead className="text-slate-400">CNIC</TableHead>
                    <TableHead className="text-slate-400">Total Invested</TableHead>
                    <TableHead className="text-slate-400">Profit / Freq</TableHead>
                    <TableHead className="text-slate-400">Mobile</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} className="border-slate-800" data-testid="investor-row">
                      <TableCell className="text-amber-500 font-mono text-xs" data-testid={`investor-account-${r.id}`}>{r.account_number || '—'}</TableCell>
                      <TableCell className="text-white font-medium">{r.investment_name}</TableCell>
                      <TableCell className="text-white">{r.name}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{r.cnic}</TableCell>
                      <TableCell className="text-emerald-500">PKR {r.total_invested?.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-300 text-xs">PKR {r.profit_amount?.toLocaleString()} / {r.profit_frequency}</TableCell>
                      <TableCell className="text-slate-300">{r.mobile}</TableCell>
                      <TableCell>
                        <div className="flex space-x-1">
                          <Button size="sm" variant="ghost"
                            onClick={() => setTx({ open: true, id: r.id, type: 'additional_investment', amount: '', note: '' })}
                            data-testid={`add-tx-${r.id}`}
                            className="text-emerald-500 hover:bg-slate-800 h-8 w-8 p-0" title="Add transaction">
                            <TrendingUp className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openHistory(r.id)}
                            data-testid={`history-${r.id}`}
                            className="text-blue-400 hover:bg-slate-800 h-8 w-8 p-0" title="History">
                            <History className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => printStatement(r.id, 'view')}
                            data-testid={`statement-view-${r.id}`}
                            className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 w-8 p-0" title="View Statement">
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => printStatement(r.id, 'print')}
                            data-testid={`statement-print-${r.id}`}
                            className="text-amber-500 hover:bg-slate-800 h-8 w-8 p-0" title="Print Statement">
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => setSlip({ open: true, id: r.id, slip_type: 'credit', amount: '', amount_words: '', checked_by: '', authority_name: '' })}
                            data-testid={`slip-${r.id}`}
                            className="text-amber-500 hover:bg-slate-800 h-8 w-8 p-0" title="Generate slip">
                            <FileDown className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <Button size="sm" variant="ghost"
                              onClick={() => handleDelete(r)}
                              data-testid={`delete-investor-${r.id}`}
                              className="text-red-500 hover:text-red-400 hover:bg-slate-800 h-8 w-8 p-0" title="Delete investor (Super Admin only)">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      <Dialog open={tx.open} onOpenChange={(o) => setTx({ ...tx, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader><DialogTitle>Log Transaction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={tx.type} onValueChange={(v) => setTx({ ...tx, type: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="tx-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="additional_investment">Additional Investment</SelectItem>
                <SelectItem value="profit_credit">Profit Credit</SelectItem>
                <SelectItem value="withdrawal">Withdrawal</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={tx.amount} placeholder="Amount"
              data-testid="tx-amount"
              onChange={(e) => setTx({ ...tx, amount: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Textarea value={tx.note} placeholder="Note"
              onChange={(e) => setTx({ ...tx, note: e.target.value })} className="bg-slate-800 border-slate-700 text-white" rows={2} />
            <div className="flex justify-end">
              <Button onClick={submitTx} data-testid="submit-tx" className="bg-emerald-600 hover:bg-emerald-700">Record</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={slip.open} onOpenChange={(o) => setSlip({ ...slip, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader><DialogTitle>Generate Slip</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={slip.slip_type} onValueChange={(v) => setSlip({ ...slip, slip_type: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="slip-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="credit">Credit Slip</SelectItem>
                <SelectItem value="debit">Debit Slip</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={slip.amount} placeholder="Amount (PKR)"
              data-testid="slip-amount"
              onChange={(e) => setSlip({ ...slip, amount: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input value={slip.amount_words} placeholder="Amount in words"
              data-testid="slip-words"
              onChange={(e) => setSlip({ ...slip, amount_words: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input value={slip.checked_by} placeholder="Checked By"
              onChange={(e) => setSlip({ ...slip, checked_by: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input value={slip.authority_name} placeholder="Authority Officer Name"
              onChange={(e) => setSlip({ ...slip, authority_name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <div className="flex justify-end">
              <Button onClick={submitSlip} data-testid="submit-slip" className="bg-amber-500 hover:bg-amber-600">
                <FileDown className="h-4 w-4 mr-2" /> Generate & Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={history.open} onOpenChange={(o) => setHistory({ ...history, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl">
          <DialogHeader><DialogTitle>Transaction History</DialogTitle></DialogHeader>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400">Type</TableHead>
                <TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">By</TableHead>
                <TableHead className="text-slate-400">Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.rows.map((t) => (
                <TableRow key={t.id} className="border-slate-800">
                  <TableCell className="text-slate-300 text-xs">{t.at ? new Date(t.at).toLocaleString() : '-'}</TableCell>
                  <TableCell className="text-white text-xs">{t.type}</TableCell>
                  <TableCell className="text-emerald-500">PKR {t.amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-slate-300 text-xs">{t.actor_name}</TableCell>
                  <TableCell className="text-slate-400 text-xs">{t.note || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
